// Core
import { 
	Box, 
	Image, 
	Divider, 
	Button, 
	Grid, 
	Text, 
	Flex, 
	FormControl,
	Textarea,
	useColorModeValue,
	useColorMode
} from '@chakra-ui/react';
import { useHistory, useParams } from 'react-router-dom';
import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';

// Assets
import { formatDateTicket, getInitials } from 'services/Utils';
import { apiService } from 'services/ApiService';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import crystalsL from 'assets/img/details/crystals-l.png';
import crystalsR from 'assets/img/details/crystals-r.png';

// Components
import { LinkIcon, StarsIcon, EntryArrowIcon, SendMessageIcon } from 'components/Icons/Icons';
import Card from 'components/Card/Card';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';

const Livechat = ({ userData }) => {
	const { id } = useParams();

  	const bg = useColorModeValue('#E0EAFF', '#050A19');
	const border = useColorModeValue('1px solid #2ABDFD', '1px solid #2ABDFD');
	const borderDivider = useColorModeValue('#E0EAFF', '#050B21');
	const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');
	const bgDividerDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #9EDFFF 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
	const bgDividerWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
	const bgDivider = useColorModeValue(bgDividerWhite, bgDividerDark);
	const textColor = useColorModeValue('#0E1633', 'white');
	const bgColorAdmin = useColorModeValue('#F5FAFF', '#0E1633');
	const bgColorUser = useColorModeValue('white', '#0D234D');

	const { colorMode } = useColorMode();
  	const isDark = colorMode === 'dark';

	const chatRef = useRef(null);

	const [data, setData] = useState([]);
	const { isLoading, refetch } = useQuery({
        queryKey: ['messages', id],
        queryFn: async () => {
            const response = await apiService.getTicketMessages({id});
            setData(response.data);
            return true;
        },
        refetchOnWindowFocus: false,
        refetchInterval: 5000,
    });

    const [message, setMessage] = useState();
    const [isValidInputMessage, setIsValidInputMessage] = useState(true);  
    const handleSendMsg = (event) => {
    	const value = event.target.value;
    	setMessage(value);
    	setIsValidInputMessage(true);  
    };

    const sendMsg = async () => {
    	if(message?.length < 1){ 
    		setIsValidInputMessage(false);
    		return;
    	}

    	try {
	      const body = {
	      	id,
	        message
	      };

	      const response = await apiService.createTicketMessages(body);
	      if(response.data === false || response.statusCode !== 200){
	      	setIsValidInputMessage(false);
	        return;
	      }
	    } catch(e){
	      setIsValidInputMessage(false);
	      return;
	    }

	    setTimeout(() => { refetch(); }, 500);
	    setMessage('');
	    setIsValidInputMessage(true);
    };

    useEffect(() => {
	  if (chatRef.current) {
	    setTimeout(() => {
	      chatRef.current.scrollTop = chatRef.current.scrollHeight;
	    }, 100); 
	  }
	}, [data, isLoading]);

	return (
		<Box mt="24px">
			<Breadcrumbs target='Request history' url='/contact-support' current='Livechat' />

			<Box maxW="564px" m="24px auto 100px auto" position="relative">
				<Box bg="#099BFF" borderRadius="full" top="50%" transform="translate(0, -50%)" filter="blur(100px)" w="50%" h="50%" position="absolute" left="-30%" />
				<Box bg="#099BFF" borderRadius="full" top="50%" transform="translate(0, -50%)"filter="blur(100px)" w="50%" h="50%" position="absolute" right="-30%" />

				<Image src={crystalsL} alt="Crystal" position="absolute" left="-27%" top="50%" transform="translate(0, -50%)" />
				<Image src={crystalsR} alt="Crystal" position="absolute" right="-27%" top="50%" transform="translate(0, -50%)" />

				<Card padding="20px" border={border} bg={bg} boxShadow={boxShadow} overflow="hidden">
					<Box overflow="hidden">
					    { isDark && <Box bg="#099BFF" borderRadius="full" 
							top="-20%" transform="translate(-50%, 0)" 
							filter="blur(100px)" w="50%" h="50%" opacity="0.3" position="absolute" left="50%" /> }

						<Flex zIndex="1" mt="-6px" mb="16px" gap="8px" position="relative" justifyContent="center" >
						  <StarsIcon mt="14px" fontSize="16px" />
				          <Text variant="header" fontSize="36px">
				           Livechat
				          </Text>
				          <StarsIcon mt="14px" fontSize="16px" />
				        </Flex>

				        <Text
				        	zIndex="1"
				        	textAlign="center" 
				        	variant="title-md" 
				        	fontSize="15px"
				        	mb="16px"
				        	p={{ base: '0', md: '0 20%' }}
				        	fontWeight="400">
				        	Describe your issue or question, and the assistant will be able to help you.
				        </Text>

				        <Divider mb="-4px" h="2px" border="unset" borderColor={borderDivider} bg={bgDivider} />

						<Box ref={chatRef} h="423px" overflow="auto" pt="20px">
						{ !isLoading && data.map((item, index) => (
							<Flex
								position="relative"
								key={index}
								justifyContent={ item?.from === userData?.id ? 'start' : 'end' }
								gap="8px"
								mb="8px">

								{ item?.from === userData?.id && (
									<Box w="40px">
										{ userData?.image?.length > 3 ? (
						                    <Image src={userData?.image} w="40px" h="40px" objectFit="cover" borderRadius="full" />
						                ) : (
						                    <Box className={userData?.gender === 'Male' ? 
							                    'gender-male' : userData?.gender === 'Female' ? 
							                    'gender-female' : userData?.gender === 'Transgender' ? 
							                    'gender-other' : 'gender-none' } maxW="100%" h="100%" borderRadius="full">
						                      <Text pt="12px !important">{getInitials(userData?.firstName, userData?.lastName)}</Text>
						                    </Box>
						                )}
									</Box>
								)}

								<Flex
									borderRadius={ item?.from === userData?.id ? '12px 12px 12px 0px' : '12px 12px 0px 12px' }
									p="10px 12px 12px 12px"
									position="relative"
									gap="12px"
									alignItems="end"
									bg={ item?.from === userData?.id ? bgColorUser : bgColorAdmin }>
									<Text color={textColor} wordBreak="break-all">{item?.message}</Text>
									<Text 
										whiteSpace="nowrap"
										variant="text-xs" 
										color="#9FABCB">
										{formatDateTicket(item?.createdAt)}
									</Text>
								</Flex>

							</Flex>
						))}
						</Box>
					</Box>

					<FormControl id="message" mt="28px" position="relative">
			          <Textarea
    					p="16px 40px 16px 12px !important"
			            value={message}
			            onChange={handleSendMsg}
			            className={isValidInputMessage ? 'input-valid' : 'input-invalid'}
			            placeholder='Write a message'
			            autoComplete="off"
			          />
			          <SendMessageIcon 
			          	zIndex="1" 
			          	position="absolute" 
			          	fontSize="24px"
			          	onClick={sendMsg}
			          	cursor="pointer" 
			          	right="16px" 
			          	top="16px"  />
			        </FormControl>
				</Card>
			</Box>
		</Box>
	);
};

export default Livechat;